import { useMemo, useState } from "react";
import { focusBlocks, type TaskInput, type TaskProposal } from "@habitos/shared";
import { useAuth } from "../lib/auth";
import { formatDuration, relativeDayLabel } from "../lib/format";

interface Props {
  proposal: TaskProposal;
  busy: boolean;
  onAccept: (tasks: TaskInput[]) => void;
  onRetry: () => void;
}

/** A long plan shows its first days; the rest open on demand. */
const FIRST_DAYS = 7;

/** Shows the tasks the assistant proposed, grouped by day. Nothing is saved until accepted. */
export function TaskReview({ proposal, busy, onAccept, onRetry }: Props) {
  const { today } = useAuth();
  const [off, setOff] = useState<Set<number>>(new Set());
  const [all, setAll] = useState(false);
  const chosen = proposal.tasks.filter((_, i) => !off.has(i));
  const byDay = useMemo(() => {
    const groups = new Map<string, number[]>();
    proposal.tasks.forEach((t, i) => groups.set(t.date, [...(groups.get(t.date) ?? []), i]));
    return [...groups];
  }, [proposal.tasks]);
  const shown = all ? byDay : byDay.slice(0, FIRST_DAYS);

  const toggle = (i: number) =>
    setOff((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className="stack">
      <section className="card">
        <p className="summary">{proposal.summary}</p>
      </section>
      {shown.map(([day, indexes]) => (
        <div key={day}>
          <h2 className="section">{relativeDayLabel(day, today)}</h2>
          <ul className="list">
            {indexes.map((i) => {
              const task = proposal.tasks[i]!;
              return (
                <li key={i}>
                  <label className={`step card ${off.has(i) ? "off" : ""}`} style={{ borderLeftColor: task.color }}>
                    <input type="checkbox" checked={!off.has(i)} onChange={() => toggle(i)} />
                    <div className="grow">
                      <div className="habit-title">{task.emoji} {task.title}</div>
                      <div className="muted small">
                        {task.time ?? "Sin hora"} · {formatDuration(task.durationMinutes)}
                        {task.focus && ` · ${focusBlocks(task).filter((b) => b.kind === "work").length} bloques con pausas`}
                      </div>
                      {task.prepDaysBefore && (
                        <div className="muted small">
                          🔔 {task.prepNote ?? "Preparar"}: {task.prepDaysBefore === 1 ? "el día antes" : `${task.prepDaysBefore} días antes`}
                        </div>
                      )}
                      {task.notes && <p className="small step-why">{task.notes}</p>}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {shown.length < byDay.length && (
        <button className="btn" onClick={() => setAll(true)}>
          Ver los {byDay.length - shown.length} días restantes
        </button>
      )}
      <p className="muted small">Después puedes cambiar cualquier tarea desde Hoy.</p>
      <div className="row gap">
        <button className="btn" onClick={onRetry} disabled={busy}>Otra propuesta</button>
        <button className="btn primary grow" disabled={busy || chosen.length === 0} onClick={() => onAccept(chosen)}>
          {busy ? "Guardando…" : byDay.length > 1 ? `Guardar ${chosen.length} tareas` : "Guardar en mi día"}
        </button>
      </div>
    </div>
  );
}
